import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, CalendarDays, MapPin } from 'lucide-react';
import { db } from '../lib/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';

interface ConfirmedFestival {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  location?: string;
  applicationId: string;
}

const MONTHS_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const DAYS_FR = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

export function CalendarWidget({ userId }: { userId: string }) {
  const navigate = useNavigate();
  const [festivals, setFestivals] = useState<ConfirmedFestival[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewDate, setViewDate] = useState(new Date());

  useEffect(() => {
    async function load() {
      try {
        const appsSnap = await getDocs(
          query(collection(db, 'applications'), where('applicantId', '==', userId), where('status', '==', 'confirme'))
        );
        const results: ConfirmedFestival[] = [];
        await Promise.all(appsSnap.docs.map(async (appDoc) => {
          const app = appDoc.data();
          if (!app.festivalId) return;
          // Festival data may already be on the application doc (denormalized)
          // or we fetch from festivals collection
          const festSnap = await getDocs(
            query(collection(db, 'festivals'), where('__name__', '==', app.festivalId))
          );
          if (!festSnap.empty) {
            const fest = festSnap.docs[0].data();
            if (fest.startDate && fest.endDate) {
              results.push({
                id: app.festivalId,
                name: fest.name || app.standName || 'Festival',
                startDate: fest.startDate,
                endDate: fest.endDate,
                location: fest.location,
                applicationId: appDoc.id,
              });
            }
          }
        }));
        results.sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
        setFestivals(results);
      } catch (e) {
        console.error('CalendarWidget:', e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [userId]);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  // Days in current view with a festival
  const eventDays = new Set<number>();
  festivals.forEach(f => {
    const start = new Date(f.startDate);
    const end = new Date(f.endDate);
    const cur = new Date(start);
    while (cur <= end) {
      if (cur.getFullYear() === year && cur.getMonth() === month) {
        eventDays.add(cur.getDate());
      }
      cur.setDate(cur.getDate() + 1);
    }
  });

  const firstDayOfMonth = new Date(year, month, 1).getDay();
  // Sunday = 0 → shift to Monday-first (Mon=0 … Sun=6)
  const startOffset = (firstDayOfMonth + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  const isThisMonth = today.getFullYear() === year && today.getMonth() === month;

  const prevMonth = () => setViewDate(new Date(year, month - 1, 1));
  const nextMonth = () => setViewDate(new Date(year, month + 1, 1));

  // Upcoming confirmed festivals from now
  const upcoming = festivals.filter(f => new Date(f.endDate) >= today);

  const fmtDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="mt-4 bg-[#161618] rounded-[1.5rem] p-5 border border-white/5 animate-pulse h-36" />
    );
  }

  return (
    <div className="mt-4 bg-[#161618] rounded-[1.5rem] p-5 border border-white/5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-[#FF5C00]" />
          <span className="text-sm font-black text-white">Mes festivals</span>
          {upcoming.length > 0 && (
            <span className="bg-[#FF5C00]/15 text-[#FF5C00] text-[10px] font-black px-1.5 py-0.5 rounded-full">
              {upcoming.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={prevMonth} className="p-1 text-gray-600 hover:text-white transition-colors rounded-lg hover:bg-white/5">
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <span className="text-xs font-bold text-gray-400 w-24 text-center">
            {MONTHS_FR[month].slice(0, 3)} {year}
          </span>
          <button onClick={nextMonth} className="p-1 text-gray-600 hover:text-white transition-colors rounded-lg hover:bg-white/5">
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Calendar grid */}
      <div className="mb-4">
        {/* Day labels */}
        <div className="grid grid-cols-7 mb-1">
          {DAYS_FR.map((d, i) => (
            <div key={i} className="text-center text-[10px] font-bold text-gray-600 py-0.5">{d}</div>
          ))}
        </div>
        {/* Day cells */}
        <div className="grid grid-cols-7 gap-y-0.5">
          {Array(startOffset).fill(null).map((_, i) => <div key={`e-${i}`} />)}
          {Array(daysInMonth).fill(null).map((_, i) => {
            const day = i + 1;
            const hasEvent = eventDays.has(day);
            const isToday = isThisMonth && today.getDate() === day;
            return (
              <div key={day} className="flex flex-col items-center py-0.5">
                <div className={`w-6 h-6 flex items-center justify-center rounded-full text-[11px] font-bold transition-all
                  ${isToday && hasEvent ? 'bg-[#FF5C00] text-white' :
                    isToday ? 'bg-white/10 text-white' :
                    hasEvent ? 'text-[#FF5C00]' :
                    'text-gray-500'}`}>
                  {day}
                </div>
                {hasEvent && !isToday && (
                  <div className="w-1 h-1 rounded-full bg-[#FF5C00] mt-0.5" />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Upcoming list */}
      {upcoming.length === 0 ? (
        <div className="text-center py-3 border-t border-white/5">
          <p className="text-xs text-gray-600">Aucune réservation à venir</p>
          <button
            onClick={() => navigate('/home')}
            className="mt-1.5 text-[10px] font-black text-[#FF5C00] hover:underline"
          >
            Explorer les festivals →
          </button>
        </div>
      ) : (
        <div className="border-t border-white/5 pt-3 space-y-2.5">
          {upcoming.slice(0, 3).map(f => (
            <button
              key={f.applicationId}
              onClick={() => navigate('/stand/festivals')}
              className="w-full flex items-start gap-2.5 text-left group"
            >
              <div className="shrink-0 w-8 h-8 rounded-xl bg-[#FF5C00]/10 border border-[#FF5C00]/20 flex items-center justify-center">
                <CalendarDays className="w-3.5 h-3.5 text-[#FF5C00]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-black text-white truncate group-hover:text-[#FF5C00] transition-colors">{f.name}</p>
                <p className="text-[10px] text-gray-500 mt-0.5">{fmtDate(f.startDate)} → {fmtDate(f.endDate)}</p>
                {f.location && (
                  <div className="flex items-center gap-1 mt-0.5">
                    <MapPin className="w-2.5 h-2.5 text-gray-600 shrink-0" />
                    <p className="text-[10px] text-gray-600 truncate">{f.location.split(',')[0]}</p>
                  </div>
                )}
              </div>
            </button>
          ))}
          {upcoming.length > 3 && (
            <button onClick={() => navigate('/stand/festivals')} className="w-full text-center text-[10px] font-black text-gray-600 hover:text-[#FF5C00] transition-colors pt-1">
              +{upcoming.length - 3} autre{upcoming.length - 3 > 1 ? 's' : ''} →
            </button>
          )}
        </div>
      )}
    </div>
  );
}
